import {
  AccountName,
  BacktestingFilterStatus,
  BaseFollowSignalData,
  BasicBotData,
  BOOLEAN_FILTER_TYPES,
  BooleanFilterType,
  BotStatus,
  CHECKBOX_FILTER_TYPES,
  CheckboxFilterType,
  ClientStatus,
  CoinExtraExchangesData,
  DefaultTimeFrame,
  Direction,
  DisplayAmountType,
  EntryZoneCategory,
  ExchangeName,
  ExchangeToClientsMapping,
  ExchangeToSymbolsMapping,
  FiltersData,
  FiltersRequest,
  FiltersSliceState,
  FilterType,
  FixMeLater,
  GroupFilterData,
  LinkedFiltersType,
  MarginTypes,
  MarketplaceSubGroup,
  RANGE_FILTER_TYPES,
  RangeFilterType,
  RawFiltersData,
  SLIDER_FILTER_TYPES,
  SliderFilterType,
  Status,
  StringArrayFilterType,
  SymbolToExchangesMapping,
  TagName,
  TradeData,
} from "@shared/types";
import { isStringsArray } from "@shared/types/utils";
import {
  difference,
  get,
  intersection,
  isNumber,
  max,
  mergeWith,
  min,
  union,
  uniq,
} from "lodash";
import pluralize from "pluralize";
import {
  FILTER_TYPE_TO_REQUEST_KEY,
  FILTER_TYPES_DATA,
} from "@shared/features/filters/constants";
import {
  MARGIN_TYPE_TO_DISPLAY,
  TAG_NAME_TO_PROPERTY_NAME,
} from "@shared/common/constants";
import { FollowSignalStatus } from "@shared/features/signals/common/types";
import { sortByStringProperty } from "@shared/utils/general";
import { INACTIVE_ACCOUNT_STATUSES } from "@shared/features/portfolio/constants";

export const getAllSelectedDisplay = (
  title: string,
  isSingleRowView?: boolean,
  usePluralize: boolean = true
) => {
  return `All${
    isSingleRowView
      ? ` ${
          usePluralize ? pluralize(title.toLowerCase()) : title.toLowerCase()
        }`
      : ""
  }`;
};

export const getSliderValueDisplay = (
  filterType: FilterType,
  value?: [number, number] | [],
  options?: [number, number]
) => {
  const start = value?.[0] ?? options?.[0];
  const end = value?.[1] ?? options?.[1];
  if (filterType === FilterType.LEVERAGE) {
    return `${start}x - ${end}x`;
  } else if (filterType === FilterType.CONSECUTIVE_ACTIVE_DAYS) {
    return `${start} - ${end} days`;
  } else if (filterType === FilterType.USD_PRICE_RANGE) {
    return `$${start} - $${end}`;
  } else if (filterType === FilterType.AVG_NUM_SIGNALS) {
    return `${start} - ${end} signals`;
  } else if (filterType === FilterType.PRICE_RANGE) {
    return `${"$".repeat(start || 0)} - ${"$".repeat(end || 0)}`;
  }
};

export const filterByStatus = <T extends { client_status?: ClientStatus }>(
  data: T[],
  statusFilter: string[]
): T[] => {
  if (statusFilter.length === 0) return [];
  return data.filter((account) => {
    let convertedClientStatus: Status;
    if (account.client_status === ClientStatus.VALID)
      convertedClientStatus = Status.ACTIVE;
    else if (
      INACTIVE_ACCOUNT_STATUSES.has(account.client_status as ClientStatus)
    )
      convertedClientStatus = Status.INACTIVE;
    else convertedClientStatus = Status.INVALID;
    return (
      !account.client_status || statusFilter.includes(convertedClientStatus)
    );
  });
};

export const filterByClients = <T extends { client: string }>(
  data: T[],
  clientsFilter: AccountName[]
): T[] => {
  if (clientsFilter.length === 0) return [];
  return data.filter((coinData) => clientsFilter.includes(coinData.client));
};

export const filterByExchanges = <
  T extends { supported_exchanges: ExchangeName[] }
>(
  data: T[],
  exchangesFilter: ExchangeName[]
): T[] => {
  if (exchangesFilter.length === 0) return [];
  return data.filter((subGroupData) =>
    subGroupData.supported_exchanges.some((exchange) =>
      exchangesFilter.includes(exchange)
    )
  );
};

export const testFollowStatusFilter = <T extends BaseFollowSignalData>(
  followStatus: string[] | undefined,
  data: T
): boolean => {
  const tradesExists =
    data.trade_exists?.length || data.bot_trade_exists?.length;
  if (followStatus?.includes(FollowSignalStatus.FOLLOWING) && tradesExists)
    return true;
  else if (
    followStatus?.includes(FollowSignalStatus.NOT_FOLLOWING) &&
    !tradesExists
  )
    return true;
  return false;
};

export const filterByPrice = (
  data: MarketplaceSubGroup[],
  priceFilter?: [number, number] | []
) => {
  if (!priceFilter || priceFilter.length === 0) return [];

  return data.filter((subGroupData) => {
    return (
      subGroupData.price_number >= priceFilter[0] &&
      subGroupData.price_number <= priceFilter[1]
    );
  });
};

export const getSourceTypesToSourcesMapping = (data?: RawFiltersData) => {
  const sourceTypesMapping = data?.values?.["Source Type"] || {};
  const sourceTypes = Object.keys(sourceTypesMapping);
  return sourceTypes.reduce((acc: Record<string, string[]>, sourceType) => {
    acc[sourceType] = get(sourceTypesMapping, [sourceType, "Source"], []);
    return acc;
  }, {});
};

export const getExchangesToClientsMapping = (
  data?: RawFiltersData
): ExchangeToClientsMapping => {
  const exchangesMapping = data?.values?.Exchanges || {};
  const exchangesNames = Object.keys(exchangesMapping);
  return exchangesNames.reduce(
    (acc: ExchangeToClientsMapping, exchangeName) => {
      const exchangeData = exchangesMapping[exchangeName];
      if (Array.isArray(exchangeData)) {
        acc[exchangeName] = exchangeData;
      } else {
        acc[exchangeName] = exchangeData?.["Accounts"];
      }
      return acc;
    },
    {}
  );
};

export const getSingleExchangeToClientMapping = (
  data: CoinExtraExchangesData
): Record<string, string[]> => {
  let mapping: Record<string, string[]> = {};
  const dataKeys = Object.keys(data);
  const exchange = dataKeys.find((key) => key !== "total");
  if (exchange) {
    mapping[exchange] = Object.keys(data[exchange]);
  }
  return mapping;
};

export const getExchangeToSymbolsMapping = (
  data?: RawFiltersData
): ExchangeToSymbolsMapping => {
  const exchangesMapping = data?.values?.Exchanges || {};
  return Object.keys(exchangesMapping).reduce(
    (acc: ExchangeToSymbolsMapping, exchangeName) => {
      acc[exchangeName] = get(exchangesMapping, [exchangeName, "Symbols"], []);
      return acc;
    },
    {}
  );
};

export const getClientToSymbolsMapping = (
  data?: RawFiltersData
): ExchangeToSymbolsMapping => {
  const exchangesMapping = data?.values?.Exchanges || {};
  return Object.keys(exchangesMapping).reduce(
    (acc: ExchangeToSymbolsMapping, exchangeName) => {
      const accounts: string[] = get(
        exchangesMapping,
        [exchangeName, "Accounts"],
        []
      );
      const symbols = get(exchangesMapping, [exchangeName, "Symbols"], []);
      accounts.forEach((account) => {
        acc[account] = symbols;
      });
      return acc;
    },
    {}
  );
};

export const splitMapToKeysAndEntries = (map: {
  [key: string]: string[] | undefined;
}): [string[], string[]] => {
  const keys = Object.keys(map);
  const entries = Object.values(map).reduce((acc: string[], entry) => {
    if (entry) {
      acc.push(...entry);
    }
    return acc;
  }, []);
  return [keys, uniq(entries)];
};

export const flipStringToStringArrayMap = (map: {
  [key: string]: string[];
}): Record<string, string[]> => {
  return Object.entries(map).reduce(
    (acc: SymbolToExchangesMapping, [key, entries]) => {
      entries.forEach((entry) => {
        if (entry in acc) {
          acc[entry] = [...acc[entry], key];
        } else {
          acc[entry] = [key];
        }
      });
      return acc;
    },
    {}
  );
};

const loadFilter = (
  filterType: FilterType,
  state: FiltersSliceState,
  loadOptions: boolean,
  loadValues: boolean,
  options?: FixMeLater,
  onlyAddOptions?: boolean,
  value?: FixMeLater
) => {
  if (
    filterType === FilterType.IS_HOT ||
    filterType === FilterType.GROUPS ||
    filterType === FilterType.VALID_BOT_AMOUNT
  )
    return;
  let filterData = state.filters[filterType];
  if (
    (!options?.length && !FILTER_TYPES_DATA[filterType].showEmptyOptions) ||
    (options?.length === 1 && !FILTER_TYPES_DATA[filterType].showSingleOption)
  ) {
    if (filterData && !onlyAddOptions) {
      delete state.filters[filterType];
    }
    return;
  }

  let filterOptions = filterData?.options;
  let addedOptions: string[] | undefined;
  let updatedOptions: any = options;
  if (loadOptions) {
    if (isStringsArray(updatedOptions) && isStringsArray(filterOptions)) {
      addedOptions = difference(updatedOptions, filterOptions);
      if (onlyAddOptions) {
        updatedOptions = [...filterOptions, ...addedOptions];
      }
    }
    filterOptions = updatedOptions;
  }

  let filterValue = filterData?.value;
  if (loadValues) {
    filterValue = value ?? (filterOptions as FixMeLater);
  } else if (isStringsArray(filterValue) && isStringsArray(filterOptions)) {
    filterValue = intersection(filterValue, filterOptions);
  }
  if (addedOptions && (isStringsArray(filterValue) || !filterValue)) {
    filterValue = union(filterValue || [], addedOptions);
  }

  if (filterOptions) {
    state.filters[filterType] = {
      value: filterValue ?? filterOptions,
      options: filterOptions,
    } as FixMeLater;
  }
};

const loadRangeFilter = (
  filterType: RangeFilterType,
  state: FiltersSliceState,
  loadOptions: boolean,
  loadValues: boolean,
  rawOptions: { min?: number; max?: number } | undefined,
  onlyAddOptions: boolean | undefined
) => {
  const filterData = state.filters[filterType];
  let filterOptions = filterData?.options;

  if (
    (!rawOptions && !FILTER_TYPES_DATA[filterType].showEmptyOptions) ||
    (rawOptions && (!isNumber(rawOptions?.min) || !isNumber(rawOptions?.max)))
  )
    return;

  let options = rawOptions ? [rawOptions.min, rawOptions.max] : [];
  let rangeOptions = options;
  if (loadOptions && filterOptions) {
    options = [...options, ...filterOptions];
    rangeOptions = [min(options), max(options)];
  }

  const shouldLoadValue =
    loadValues || getIsAllSelected(filterType, filterData);

  loadFilter(
    filterType,
    state,
    loadOptions,
    shouldLoadValue,
    rangeOptions,
    onlyAddOptions
  );
};

const loadBooleanFilter = (
  state: FiltersSliceState,
  filterType: BooleanFilterType,
  loadValues: boolean,
  defaultValue: boolean = false
) => {
  const filterData = state.filters[filterType] || {
    value: false,
  };
  if (loadValues) {
    filterData.value = defaultValue;
  }
  state.filters[filterType] = filterData;
};

const loadTimeFrameFilter = (state: FiltersSliceState, loadValues: boolean) => {
  let filterValue = state.filters[FilterType.TIME_FRAME]?.value;
  if (loadValues) {
    filterValue = {
      range: [],
    };
  }
  state.filters[FilterType.TIME_FRAME] = {
    value: filterValue ?? {
      range: [],
    },
    options: [],
  } as FixMeLater;
};

export const loadGroupsFilter = (
  state: FiltersSliceState,
  groupsData: GroupFilterData[],
  isMultiGroups?: boolean
) => {
  const groupsFilter = state.filters[FilterType.GROUPS] || {
    options: [],
    value: [],
    groupIdToData: {},
  };
  const sortedGroupsData = groupsData
    .slice()
    .sort((a, b) => sortByStringProperty(a, b, "name"));
  groupsFilter.options = sortedGroupsData.map((groupData) => groupData.id);
  if (isMultiGroups) {
    groupsFilter.value = groupsFilter.options;
  } else {
    const selectedGroupIsValid = groupsData.find(
      (group) => group.id === groupsFilter.value[0]
    );
    const shouldInitializeGroupsFilter =
      sortedGroupsData.length &&
      (!groupsFilter.value.length || !selectedGroupIsValid);
    if (sortedGroupsData.length && shouldInitializeGroupsFilter) {
      groupsFilter.value = [sortedGroupsData[0].id];
    }
  }
  groupsFilter.groupIdToData = sortedGroupsData.reduce((acc, data) => {
    acc[data.id] = data;
    return acc;
  }, {} as Record<number, GroupFilterData>);
  state.filters[isMultiGroups ? FilterType.MULTI_GROUPS : FilterType.GROUPS] =
    groupsFilter;
};

export const loadFilters = (
  state: FiltersSliceState,
  filtersToLoad: FilterType[],
  rawFilters: RawFiltersData | undefined,
  loadOptions: boolean,
  loadValues: boolean,
  onlyAddOptions: boolean | undefined
): void => {
  let exchanges: string[] = [],
    clients: string[] = [],
    symbols: string[] = [],
    sources: string[] = [],
    sourceTypes: string[] = [];

  const loadClients = filtersToLoad?.includes(FilterType.CLIENTS);
  const loadExchanges = filtersToLoad?.includes(FilterType.EXCHANGES);
  if (
    (loadClients || loadExchanges) &&
    !Array.isArray(rawFilters?.values.Exchanges)
  ) {
    const exchangeToClientsMapping = getExchangesToClientsMapping(rawFilters);
    state.mappings.exchangeToClientsMapping = exchangeToClientsMapping;
    [exchanges, clients] = splitMapToKeysAndEntries(exchangeToClientsMapping);
  } else if (loadExchanges && Array.isArray(rawFilters?.values.Exchanges)) {
    exchanges = rawFilters?.values.Exchanges || [];
  }

  if (filtersToLoad?.includes(FilterType.SYMBOLS)) {
    if (rawFilters?.values["Symbols"]) {
      symbols = rawFilters?.values["Symbols"];
    } else {
      const clientToSymbolsMapping = getClientToSymbolsMapping(rawFilters);
      const exchangeToSymbolsMapping = getExchangeToSymbolsMapping(rawFilters);
      symbols = splitMapToKeysAndEntries(
        loadClients ? clientToSymbolsMapping : exchangeToSymbolsMapping
      )[1];
      state.mappings.clientToSymbolsMapping = clientToSymbolsMapping;
      state.mappings.exchangeToSymbolsMapping = exchangeToSymbolsMapping;
    }
  }
  if (filtersToLoad?.includes(FilterType.SOURCE_NAME)) {
    const sourcesMapping = rawFilters?.constants["Source New"];
    const sourceIDToSource: Record<number, string> = {};
    state.mappings.sourceToSourceIDsMapping = Object.values(
      sourcesMapping || {}
    ).reduce((acc: Record<string, number[]>, sourceToID) => {
      Object.entries(sourceToID).forEach(([source, sourceID]) => {
        if (!(source in acc)) {
          acc[source] = [sourceID];
        } else {
          acc[source].push(sourceID);
        }
        sourceIDToSource[sourceID] = source;
      });
      return acc;
    }, {});
    state.mappings.sourceIDToSourceMapping = sourceIDToSource;
  }

  if (
    filtersToLoad?.includes(FilterType.SOURCE_TYPE) ||
    filtersToLoad?.includes(FilterType.SOURCE_NAME)
  ) {
    if (rawFilters?.values["Source Type"]) {
      const sourceTypesToSourcesMapping =
        getSourceTypesToSourcesMapping(rawFilters);
      state.mappings.sourceTypeToSourcesMapping = sourceTypesToSourcesMapping;
      state.mappings.sourceToSourceTypesMapping = flipStringToStringArrayMap(
        sourceTypesToSourcesMapping
      );
      [sourceTypes, sources] = splitMapToKeysAndEntries(
        sourceTypesToSourcesMapping
      );
    }
  }

  const filterTypeToOptions: { [key in FilterType]?: any } = {
    [FilterType.CONSECUTIVE_ACTIVE_DAYS]:
      rawFilters?.values["Consecutive Active Days"],
    [FilterType.LEVERAGE]: rawFilters?.values["Leverage"],
    [FilterType.USD_PRICE_RANGE]: rawFilters?.values["Price USD range"],
    [FilterType.AVG_NUM_SIGNALS]:
      rawFilters?.values["Average weekly signals range"],
    [FilterType.AVG_RATING]: [5, 4, 3, 2, 1, -1],
    [FilterType.EXCHANGES]: exchanges,
    [FilterType.CLIENTS]: clients,
    [FilterType.SYMBOLS]: symbols,
    [FilterType.LONG_SHORT]: rawFilters?.values["Long/Short"]?.length
      ? rawFilters?.values["Long/Short"]
      : [Direction.LONG, Direction.SHORT],
    [FilterType.SIGNAL_STATUS]: Object.values(EntryZoneCategory)
      .filter((val) => typeof val === "number")
      .reverse(),
    [FilterType.FOLLOW_STATUS]: [
      FollowSignalStatus.FOLLOWING,
      FollowSignalStatus.NOT_FOLLOWING,
    ],
    [FilterType.STATUS]: rawFilters?.values["Status"] ?? [
      Status.ACTIVE,
      Status.INACTIVE,
    ],
    [FilterType.MARGIN_TYPE]: rawFilters?.values["Margin Type"],
    [FilterType.TRADE_TYPE]: rawFilters?.values["Trade Type"],
    [FilterType.SOURCE_NAME]: sources,
    [FilterType.SOURCE_TYPE]: sourceTypes,
    [FilterType.BACKTESTING_STATUS]: [
      BacktestingFilterStatus.IN_PROGRESS,
      BacktestingFilterStatus.COMPLETED,
      BacktestingFilterStatus.FAILED,
    ],
  };

  Object.entries(filterTypeToOptions).forEach(([filterTypeStr, options]) => {
    const filterType = parseInt(filterTypeStr);
    if (filtersToLoad.includes(filterType)) {
      const isRangeFilter = RANGE_FILTER_TYPES.includes(filterType);
      const loadFunc = isRangeFilter ? loadRangeFilter : loadFilter;
      loadFunc(
        filterType,
        state,
        loadOptions,
        loadValues,
        options || (isRangeFilter ? undefined : []),
        onlyAddOptions
      );
    }
  });

  if (filtersToLoad.includes(FilterType.MULTI_GROUPS)) {
    const parsedGroupsData = rawFilters?.values?.["Sub Groups"]?.map(
      (groupData) => ({
        id: groupData[0],
        name: groupData[1],
        image: groupData[2],
      })
    );
    if (parsedGroupsData) {
      loadGroupsFilter(state, parsedGroupsData, true);
    }
  }

  if (filtersToLoad.includes(FilterType.TIME_FRAME)) {
    loadTimeFrameFilter(state, loadValues);
  }

  if (filtersToLoad.includes(FilterType.AMOUNT_TYPE)) {
    loadFilter(
      FilterType.AMOUNT_TYPE,
      state,
      loadOptions,
      loadValues,
      [DisplayAmountType.TOTAL, DisplayAmountType.AVAILABLE],
      undefined,
      [DisplayAmountType.TOTAL]
    );
  }

  if (filtersToLoad.includes(FilterType.TAGS)) {
    loadFilter(
      FilterType.TAGS,
      state,
      loadOptions,
      loadValues,
      state.config.configTags,
      undefined,
      FILTER_TYPES_DATA[FilterType.TAGS].defaultValue
    );
  }

  BOOLEAN_FILTER_TYPES.forEach((filterType) => {
    if (filtersToLoad.includes(filterType)) {
      loadBooleanFilter(
        state,
        filterType,
        loadValues,
        FILTER_TYPES_DATA[filterType].defaultValue || false
      );
    }
  });

  if (filtersToLoad.includes(FilterType.PRICE_RANGE)) {
    const priceRangeOptions =
      rawFilters?.values["Price range"]?.slice().sort((a, b) => a - b) || [];
    loadFilter(
      FilterType.PRICE_RANGE,
      state,
      loadOptions,
      loadValues,
      priceRangeOptions,
      undefined,
      [priceRangeOptions[0], priceRangeOptions[priceRangeOptions.length - 1]]
    );
  }

  if (state.loadedFilters && loadValues) {
    filtersToLoad.forEach((filterType) =>
      handleLinkedFilters(state, filterType)
    );
  }
};

const handleUpdatedParentFilter = (
  state: FiltersSliceState,
  parentFilterType: FilterType,
  childFilterType: FilterType,
  parentToChildMappingKey:
    | "exchangeToClientsMapping"
    | "clientToSymbolsMapping"
    | "exchangeToSymbolsMapping"
    | "sourceTypeToSourcesMapping",
  linkType: LinkedFiltersType
) => {
  const parentToChildMapping = state.mappings[parentToChildMappingKey];
  const parentFilterData = state.filters[parentFilterType];
  const childFilterData = state.filters[childFilterType];
  if (
    !parentToChildMapping ||
    !state.config.filterTypes?.includes(childFilterType) ||
    !childFilterData ||
    !parentFilterData ||
    !isStringsArray(parentFilterData.value) ||
    !isStringsArray(childFilterData.value) ||
    !("options" in childFilterData)
  )
    return;
  const validChildOptions = uniq(
    parentFilterData.value
      // @ts-ignore
      .map((parentItem: string) => parentToChildMapping[parentItem])
      .flat()
      .filter((value) => !!value) as string[]
  );
  if (linkType === LinkedFiltersType.FULL_LINK) {
    childFilterData.value = validChildOptions;
  } else {
    const validChildOptionsSet = new Set<string>(validChildOptions);
    const validChildValues = childFilterData.value.filter((value) =>
      validChildOptionsSet.has(value)
    );
    if (
      childFilterData.value.length === 0 ||
      childFilterData.value.length === childFilterData.options.length
    ) {
      childFilterData.value = validChildOptions;
    } else {
      childFilterData.value = validChildValues;
    }
    childFilterData.options = validChildOptions;
  }
};

const handleUpdatedChildFilter = (
  state: FiltersSliceState,
  parentFilterType: FilterType,
  childFilterType: FilterType,
  parentToChildMappingKey:
    | "exchangeToClientsMapping"
    | "clientToSymbolsMapping"
    | "exchangeToSymbolsMapping"
    | "sourceTypeToSourcesMapping"
) => {
  const parentToChildMapping = state.mappings[parentToChildMappingKey];
  const parentFilterData = state.filters[parentFilterType];
  const childFilterData = state.filters[childFilterType];
  if (
    !parentToChildMapping ||
    !state.config.filterTypes?.includes(parentFilterType) ||
    !childFilterData ||
    !parentFilterData ||
    !isStringsArray(parentFilterData.value) ||
    !isStringsArray(childFilterData.value) ||
    !("options" in childFilterData)
  )
    return;

  parentFilterData.value = Object.keys(parentToChildMapping).filter(
    (parentItem) => {
      const childOptions = parentToChildMapping[parentItem];
      if (Array.isArray(childOptions)) {
        return childOptions.every((childOption) =>
          (childFilterData.value as string[]).includes(childOption)
        );
      }
    }
  );
};

const handleLinkedFilters = (
  state: FiltersSliceState,
  updatedFilterType: FilterType
) => {
  Object.entries(getLinkedFiltersData(state)).forEach(
    ([parentFilterType, linksData]) => {
      linksData.forEach((linkData) => {
        if (
          linkData.childFilterType === updatedFilterType &&
          linkData.linkType === LinkedFiltersType.FULL_LINK
        ) {
          handleUpdatedChildFilter(
            state,
            parseInt(parentFilterType),
            linkData.childFilterType,
            linkData.parentToChildMappingKey
          );
        }
      });
      const parsedParentFilterType = parseInt(parentFilterType);
      if (updatedFilterType === parsedParentFilterType) {
        linksData.forEach((linkData) => {
          if (updatedFilterType !== linkData.childFilterType) {
            handleUpdatedParentFilter(
              state,
              parsedParentFilterType,
              linkData.childFilterType,
              linkData.parentToChildMappingKey,
              linkData.linkType
            );
          }
        });
      }
    }
  );
};

export const setFilterData = (
  state: FiltersSliceState,
  filterType: FilterType,
  value: any
) => {
  const filterData = state.filters[filterType];
  if (filterData) {
    filterData.value = value;
  }
  handleLinkedFilters(state, filterType);
};

export const testRangeFilter = (
  minMax: [number, number] | [] | undefined,
  value: number
) => {
  const min = minMax?.[0];
  const max = minMax?.[1];
  return (!isNumber(min) || value >= min) && (!isNumber(max) || value <= max);
};

export const testAvgRatingFilter = (
  filterValue: number[] | undefined,
  value: number | null
) => {
  if (!filterValue) return true;
  else if (value === null && filterValue.includes(-1)) return true;
  else if (value !== null) {
    const roundedValue = Math.round(value);
    return filterValue.includes(roundedValue);
  }
  return false;
};

export const testTagsFilter = (
  filterValue: string[] | undefined,
  item: MarketplaceSubGroup
) => {
  if (!filterValue || !filterValue.length) return true;
  for (let tag of filterValue) {
    if (item[TAG_NAME_TO_PROPERTY_NAME[tag as TagName]]) return true;
  }
  return false;
};

export const testStringListFilter = (
  filterValue: string[] | undefined,
  value: string
) => {
  return !filterValue || filterValue.includes(value);
};

export const testNumberListFilter = (
  filterValue: number[] | undefined,
  value?: number
) => {
  return !filterValue || !value || filterValue.includes(value);
};

const updateFiltersMappings = (
  state: FiltersSliceState,
  mappingsData: {
    exchange?: string;
    symbol?: string;
    account?: string;
    sourceType?: string;
    sourceName?: string;
  }
) => {
  const { exchange, account, symbol, sourceType, sourceName } = mappingsData;

  const mergeArrayValueObjects = (
    obj1: Record<string, string[] | undefined> | undefined,
    obj2: Record<string, string[]>
  ) => {
    return mergeWith(obj1 || {}, obj2, (obj1Value, obj2Value) =>
      union(obj1Value, obj2Value)
    );
  };

  const mappings = state.mappings;
  if (exchange && account) {
    mappings.exchangeToClientsMapping = mergeArrayValueObjects(
      state.mappings.exchangeToClientsMapping,
      { [exchange]: [account] }
    );
    mappings.clientToExchangeMapping = {
      ...state.mappings.clientToExchangeMapping,
      [account]: exchange,
    };
  }
  if (account && symbol) {
    mappings.clientToSymbolsMapping = mergeArrayValueObjects(
      state.mappings.clientToSymbolsMapping,
      { [account]: [symbol] }
    );
  }
  if (exchange && symbol) {
    mappings.exchangeToSymbolsMapping = mergeArrayValueObjects(
      state.mappings.exchangeToSymbolsMapping,
      { [exchange]: [symbol] }
    );
  }
  if (sourceType && sourceName) {
    mappings.sourceTypeToSourcesMapping = mergeArrayValueObjects(
      state.mappings.sourceTypeToSourcesMapping,
      { [sourceType]: [sourceName] }
    );
    mappings.sourceToSourceTypesMapping = mergeArrayValueObjects(
      state.mappings.sourceToSourceTypesMapping,
      { [sourceName]: [sourceType] }
    );
  }
};

export const getIsAllSelected = (filterType: FilterType, filterData: any) => {
  const filterValue = filterData?.value;
  const filterOptions =
    filterData && "options" in filterData ? filterData.options : [];

  if (filterType === FilterType.TAGS) {
    return (
      filterValue.length === FILTER_TYPES_DATA[filterType].defaultValue.length
    );
  } else if (
    RANGE_FILTER_TYPES.includes(filterType as SliderFilterType) ||
    filterType === FilterType.PRICE_RANGE
  ) {
    const parsedValue =
      filterType === FilterType.TIME_FRAME
        ? (filterValue as any)?.range || {}
        : filterValue;
    return (
      filterOptions?.[0] === parsedValue?.[0] &&
      filterOptions?.[filterOptions.length - 1] === parsedValue?.[1]
    );
  } else if (filterType === FilterType.AMOUNT_TYPE) {
    return filterValue[0] === filterOptions[0];
  } else if (BOOLEAN_FILTER_TYPES.includes(filterType as BooleanFilterType)) {
    return filterValue === FILTER_TYPES_DATA[filterType].defaultValue;
  } else if (
    CHECKBOX_FILTER_TYPES.includes(filterType as CheckboxFilterType) &&
    Array.isArray(filterValue) &&
    filterValue
  ) {
    return filterValue.length === filterOptions?.length;
  }
  return false;
};

export const getNumActiveFilters = (state: FiltersSliceState) => {
  const filterTypes = state.config.filterTypes?.filter(
    (filterType) => filterType in state.filters
  );
  return (
    filterTypes?.reduce((num, filterType) => {
      return (
        num +
        Number(
          filterType !== FilterType.GROUPS &&
            !getIsAllSelected(filterType, state.filters[filterType])
        )
      );
    }, 0) || 0
  );
};

const updateCheckboxFilters = (
  state: FiltersSliceState,
  updatedItem: any,
  items: any[],
  filterTypeToPath: {
    [key in StringArrayFilterType | FilterType.MULTI_GROUPS]?:
      | string
      | ((item: any) => string | undefined);
  }
) => {
  const getFilterValue = (
    item: any,
    filterType: StringArrayFilterType
  ): string | undefined => {
    const path = filterTypeToPath[filterType]!;
    if (!path) return;
    if (typeof path === "string") return item[path] as string;
    return path(item);
  };

  for (let filterTypeStr of Object.keys(filterTypeToPath)) {
    const filterType: StringArrayFilterType = parseInt(filterTypeStr);
    const updatedData = getFilterValue(updatedItem, filterType);
    if (!updatedData) continue;
    const currentFilterData = state.filters[filterType];
    if (currentFilterData) {
      const allSelected =
        currentFilterData.value.length === currentFilterData.options.length;
      let shouldAdd = true;
      if (filterType === FilterType.SYMBOLS) {
        const exchange = getFilterValue(updatedItem, FilterType.EXCHANGES);
        if (
          exchange &&
          !state.filters[FilterType.EXCHANGES]?.value.includes(exchange)
        ) {
          shouldAdd = false;
        }
      }
      if (shouldAdd) {
        currentFilterData.options = union(currentFilterData.options, [
          updatedData,
        ]);
        if (allSelected) {
          currentFilterData.value = union(currentFilterData.value, [
            updatedData,
          ]);
        }
      }
    } else if (!currentFilterData) {
      const options = items.map((item) => getFilterValue(item, filterType));
      const mergedOptions = union(options, [updatedData]);
      const numOptions = mergedOptions?.length || 0;
      if (
        (!numOptions && FILTER_TYPES_DATA[filterType].showEmptyOptions) ||
        (numOptions === 1 && FILTER_TYPES_DATA[filterType].showSingleOption) ||
        numOptions > 1
      ) {
        state.filters[filterType] = {
          options: mergedOptions,
          value: mergedOptions,
        } as any;
      }
    }
  }
};

const updateRangeFilters = (
  state: FiltersSliceState,
  updatedItem: any,
  items: any[],
  filterTypeToPath: {
    [key in RangeFilterType]?: string | ((item: any) => number);
  }
) => {
  const getFilterValue = (item: any, filterType: RangeFilterType): number => {
    const path = filterTypeToPath[filterType]!;
    if (typeof path === "string") return item[path] as number;
    return path(item);
  };

  for (let filterTypeStr of Object.keys(filterTypeToPath)) {
    const filterType: RangeFilterType = parseInt(filterTypeStr);
    const updatedData = getFilterValue(updatedItem, filterType);
    if (!updatedData) continue;
    const currentFilterData = state.filters[filterType];
    if (currentFilterData && Array.isArray(currentFilterData.value)) {
      const minMaxValues = currentFilterData.value;
      const allSelected =
        (!isNumber(minMaxValues[0]) ||
          minMaxValues[0] === currentFilterData.options[0]) &&
        (!isNumber(minMaxValues[1]) ||
          minMaxValues[1] === currentFilterData.options[1]);
      const minMaxOptions = currentFilterData.options;
      if (minMaxOptions.length === 2) {
        if (updatedData > minMaxOptions[1]) {
          minMaxOptions[1] = updatedData;
        } else if (updatedData < minMaxOptions[0]) {
          minMaxOptions[0] = updatedData;
        }
      }
      if (allSelected) {
        currentFilterData.value = minMaxOptions as any;
      }
    } else if (!currentFilterData) {
      const options = items.map((item) => getFilterValue(item, filterType));
      const minMax = [min(options)!, max(options)!];
      if (updatedData > minMax[1]) {
        minMax[1] = updatedData;
      } else if (updatedData < minMax[0]) {
        minMax[0] = updatedData;
      }
      if (minMax[0] !== minMax[1]) {
        state.filters[filterType] = {
          options: minMax,
          value: minMax,
        } as any;
      }
    }
  }
};

export const updateTradesFilters = (
  state: FiltersSliceState,
  updatedTrade: TradeData,
  tradesData: TradeData[]
) => {
  updateCheckboxFilters(state, updatedTrade, tradesData, {
    [FilterType.EXCHANGES]: "exchange",
    [FilterType.CLIENTS]: "account",
    [FilterType.SYMBOLS]: "symbol",
    [FilterType.LONG_SHORT]: (tradeData: TradeData) =>
      tradeData["Long/Short"] || Direction.LONG,
    [FilterType.MARGIN_TYPE]: (tradeData: TradeData) =>
      tradeData["margin_type"] || MARGIN_TYPE_TO_DISPLAY[MarginTypes.ISOLATED],
    [FilterType.SOURCE_NAME]: "group",
    [FilterType.SOURCE_TYPE]: "source_type",
    [FilterType.TRADE_TYPE]: "trade_type",
  });

  updateRangeFilters(state, updatedTrade, tradesData, {
    [FilterType.LEVERAGE]: (tradeData: TradeData) => tradeData.leverage || 1,
  });

  updateFiltersMappings(state, {
    exchange: updatedTrade.exchange,
    account: updatedTrade.account,
    sourceName: updatedTrade.group,
    sourceType: updatedTrade.source_type,
    symbol: updatedTrade.symbol,
  });
};

export const updateBotsFilters = (
  state: FiltersSliceState,
  updatedBot: BasicBotData,
  botsData: BasicBotData[]
) => {
  updateCheckboxFilters(state, updatedBot, botsData, {
    [FilterType.EXCHANGES]: "exchange",
    [FilterType.CLIENTS]: "client_name",
    [FilterType.SYMBOLS]: (botData: BasicBotData) => botData.symbols?.[0],
    [FilterType.LONG_SHORT]: (botData: BasicBotData) =>
      botData.reverse ? Direction.SHORT : Direction.LONG,
    [FilterType.STATUS]: (botData: BasicBotData) =>
      botData.status === BotStatus.ACTIVE ? Status.ACTIVE : Status.INACTIVE,
    [FilterType.MARGIN_TYPE]: (botData: BasicBotData) =>
      MARGIN_TYPE_TO_DISPLAY[botData.margin_type || MarginTypes.ISOLATED],
    // @ts-ignore
    [FilterType.MULTI_GROUPS]: (botData: BasicBotData) => botData.sub_group_id,
  });

  const multiGroupsFilter = state.filters[FilterType.MULTI_GROUPS];
  if (multiGroupsFilter && updatedBot.sub_group_id) {
    multiGroupsFilter.groupIdToData = {
      ...multiGroupsFilter.groupIdToData,
      [updatedBot.sub_group_id]: {
        id: updatedBot.sub_group_id,
        image: updatedBot.sub_group_image!,
        name: updatedBot.sub_group_name!,
      },
    };
  }

  updateRangeFilters(state, updatedBot, botsData, {
    [FilterType.LEVERAGE]: (botData: BasicBotData) => botData.leverage || 1,
  });

  updateFiltersMappings(state, {
    exchange: updatedBot.exchange,
    account: updatedBot.client_name,
    symbol: updatedBot.symbols?.[0],
  });
};

export const selectAll = (
  state: FiltersSliceState,
  filterTypes: FilterType[]
) => {
  filterTypes.forEach((filterType) => {
    if (filterType === FilterType.GROUPS) return;
    const filterData = state.filters[filterType];
    if (filterData) {
      if (filterType === FilterType.TAGS) {
        filterData.value = FILTER_TYPES_DATA[filterType].defaultValue;
      } else if (filterType === FilterType.TIME_FRAME) {
        filterData.value = {
          range: (filterData as any).options,
        };
      } else if (filterType === FilterType.AMOUNT_TYPE) {
        filterData.value = (filterData as any).options?.slice(0, 1);
      } else if (
        CHECKBOX_FILTER_TYPES.includes(filterType as CheckboxFilterType) ||
        RANGE_FILTER_TYPES.includes(filterType as RangeFilterType)
      ) {
        filterData.value = (filterData as any).options;
      } else if (
        BOOLEAN_FILTER_TYPES.includes(filterType as BooleanFilterType)
      ) {
        filterData.value = FILTER_TYPES_DATA[filterType].defaultValue || false;
      } else if (
        filterType === FilterType.PRICE_RANGE &&
        (filterData as any).options.length > 1
      ) {
        const options = (filterData as any).options;
        filterData.value = [options[0], options[options.length - 1]];
      }
    }
  });
  filterTypes.forEach((filterType) => handleLinkedFilters(state, filterType));
};

export const getChipPreviewText = <T extends FilterType>(
  filterType: T,
  filterData: FiltersData[T],
  isMobile: boolean
) => {
  let title = FILTER_TYPES_DATA[filterType].title || "";
  const allSelected = getIsAllSelected(filterType, filterData);
  const filterValue = filterData?.value;
  const filterOptions =
    filterData && "options" in filterData ? filterData.options : undefined;

  if (
    CHECKBOX_FILTER_TYPES.includes(filterType as CheckboxFilterType) &&
    Array.isArray(filterValue) &&
    Array.isArray(filterOptions) &&
    filterValue
  ) {
    if (filterValue.length === 1 && filterOptions.length > 1) {
      if (filterType === FilterType.AVG_RATING)
        return filterValue[0] === -1 ? "Unrated" : `${filterValue[0]} stars`;
      return filterValue[0];
    }
    if (filterType === FilterType.SOURCE_NAME) {
      title = "Source";
    } else if (filterType === FilterType.AVG_RATING) {
      if (allSelected) return "All ratings";
      title = "Rating";
    }
    return allSelected
      ? getAllSelectedDisplay(title, true)
      : pluralize(title, filterValue.length, true);
  } else if (SLIDER_FILTER_TYPES.includes(filterType as SliderFilterType)) {
    let inputValue: string | undefined;
    if (!allSelected && filterOptions?.length) {
      inputValue = getSliderValueDisplay(
        filterType,
        filterValue as any,
        filterOptions as [number, number]
      );
    } else if (allSelected && filterType === FilterType.USD_PRICE_RANGE) {
      inputValue = "All prices";
    } else if (allSelected && filterType === FilterType.AVG_NUM_SIGNALS) {
      inputValue = "All frequencies";
    }
    return (
      inputValue ||
      getAllSelectedDisplay(
        title,
        true,
        filterType !== FilterType.CONSECUTIVE_ACTIVE_DAYS
      )
    );
  } else if (filterType === FilterType.TIME_FRAME) {
    if (allSelected) {
      return isMobile ? "All time" : getAllSelectedDisplay(title, true);
    }
    return (filterValue as any).defaultTimeFrame || DefaultTimeFrame.ALL_TIME;
  } else if (BOOLEAN_FILTER_TYPES.includes(filterType as BooleanFilterType)) {
    if (filterType === FilterType.IS_HOT) {
      return "Hot trades";
    } else if (filterType === FilterType.VALID_BOT_AMOUNT) {
      return `Hide insufficient funds`;
    }
  } else if (filterType === FilterType.PRICE_RANGE) {
    if (allSelected) return "All prices";
    return getSliderValueDisplay(
      filterType,
      filterValue as any,
      filterOptions as [number, number]
    );
  }
  return "";
};

export const getConstantsMapping = (
  filterType: FilterType,
  rawData?: RawFiltersData
) => {
  const constantsKey = FILTER_TYPES_DATA[filterType].constantsKey;
  return constantsKey && rawData?.constants?.[constantsKey];
};

export const getValuesConstants = (
  values: any[],
  filterType: FilterType,
  filtersSlice: FiltersSliceState
) => {
  const { rawData, mappings } = filtersSlice;
  const constantsMapping = getConstantsMapping(filterType, rawData);
  if (constantsMapping && Array.isArray(values)) {
    return values
      .map((value) => {
        let constantValue: any = constantsMapping[value];
        if (filterType === FilterType.SOURCE_NAME) {
          constantValue = mappings.sourceToSourceIDsMapping?.[value];
        }
        return constantValue ?? value;
      })
      .flat();
  } else {
    return values;
  }
};

const getFilterRequestValue = (
  filterType: FilterType,
  filtersSliceState: FiltersSliceState
): any => {
  const { filters: filtersData, rawData, mappings } = filtersSliceState;
  if (getIsAllSelected(filterType, filtersData[filterType])) {
    return undefined;
  }
  const filterValue = filtersData[filterType]?.value as any;
  if (Array.isArray(filterValue)) {
    if (filterType === FilterType.SOURCE_NAME) {
      const sourceNameConstantsMapping = getConstantsMapping(
        filterType,
        rawData
      );
      const sourceTypeConstantsMapping = getConstantsMapping(
        FilterType.SOURCE_TYPE,
        rawData
      );
      if (sourceNameConstantsMapping && sourceTypeConstantsMapping) {
        return filterValue.reduce(
          (acc: Record<number, number[] | undefined>, value) => {
            const sourceTypes: string[] | undefined =
              mappings?.sourceToSourceTypesMapping?.[value];
            sourceTypes?.forEach((sourceType) => {
              const sourceNameConstant =
                // @ts-ignore
                sourceNameConstantsMapping?.[sourceType]?.[value];
              const sourceTypeConstant = sourceTypeConstantsMapping[
                sourceType
              ] as number;
              if (sourceNameConstant && sourceTypeConstant) {
                const currentSourceTypeValue = acc[sourceTypeConstant];
                if (currentSourceTypeValue) {
                  currentSourceTypeValue.push(sourceNameConstant);
                } else {
                  acc[sourceTypeConstant] = [sourceNameConstant];
                }
              }
            });
            return acc;
          },
          {}
        );
      }
    } else if (
      CHECKBOX_FILTER_TYPES.includes(filterType as CheckboxFilterType)
    ) {
      return getValuesConstants(filterValue, filterType, filtersSliceState);
    } else if (SLIDER_FILTER_TYPES.includes(filterType as SliderFilterType)) {
      return { min: filterValue[0] || null, max: filterValue[1] || null };
    }
  } else if (filterType === FilterType.TIME_FRAME) {
    const range = (filterValue as any)?.range || {};
    return { min: range[0] || null, max: range[1] || null };
  }
};

export const serializeFilters = (filtersSliceState: FiltersSliceState) => {
  const { config: filtersConfig } = filtersSliceState;
  const filterTypes = filtersConfig.filterTypes;
  return filterTypes?.reduce((acc: FiltersRequest, filterType) => {
    const requestKey = FILTER_TYPE_TO_REQUEST_KEY[filterType];
    if (requestKey) {
      const parsedValue = getFilterRequestValue(filterType, filtersSliceState);
      if (parsedValue) {
        acc[requestKey] = parsedValue;
      }
    }
    return acc;
  }, {});
};

export const isNumberArray = (array: (number | string)[]) =>
  array.every((option) => typeof option === "number");

export const getLinkedFiltersData = (filtersData: FiltersSliceState) => {
  const filterTypes = filtersData.config.filterTypes;
  const linkedData: {
    [key in FilterType]?: {
      childFilterType: FilterType;
      parentToChildMappingKey:
        | "exchangeToClientsMapping"
        | "clientToSymbolsMapping"
        | "exchangeToSymbolsMapping"
        | "sourceTypeToSourcesMapping";
      linkType: LinkedFiltersType;
    }[];
  } = {
    [FilterType.SOURCE_TYPE]: [
      {
        childFilterType: FilterType.SOURCE_NAME,
        parentToChildMappingKey: "sourceTypeToSourcesMapping",
        linkType: LinkedFiltersType.FULL_LINK,
      },
    ],
    [FilterType.EXCHANGES]: [
      {
        childFilterType: FilterType.CLIENTS,
        parentToChildMappingKey: "exchangeToClientsMapping",
        linkType: LinkedFiltersType.FULL_LINK,
      },
    ],
  };
  if (filterTypes?.includes(FilterType.CLIENTS)) {
    linkedData[FilterType.CLIENTS] = [
      {
        childFilterType: FilterType.SYMBOLS,
        parentToChildMappingKey: "clientToSymbolsMapping",
        linkType: LinkedFiltersType.OPTIONS_ONLY,
      },
    ];
  } else {
    linkedData[FilterType.EXCHANGES]?.push({
      childFilterType: FilterType.SYMBOLS,
      parentToChildMappingKey: "exchangeToSymbolsMapping",
      linkType: LinkedFiltersType.OPTIONS_ONLY,
    });
  }
  return linkedData;
};
